import React from 'react';

import { CurrencyFormat } from '@ha/intl';
import { makeStyles } from 'tss-react/mui';
import { Divider, Grid, Link, Typography, units } from '@hbf/dsl';

import { useIntl } from 'ha/i18n';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';

import { DisplayedPriceInfo } from 'ha/models/Listing/types';
import { UtilityBillsLabelByValue } from 'ha/models/ListingUtilityBills/translations';

import { InformationCircleBold } from '@ha/icons';

const useStyles = makeStyles()(theme => ({
  divider: {
    height: units.rem(1),
    borderColor: theme.palette.grey[500],
    margin: units.yx(0, 0.5),
  },
  fromLabel: {
    color: theme.palette.positive.main,
    fontFamily: sharpSansBold.fontFamily,
    fontSize: units.rem(0.75),
    textTransform: 'uppercase',
    marginRight: units.rem(0.25),
    fontWeight: 'bold',
  },
  iconInfo: {
    marginLeft: units.rem(0.25),
  },
  month: {
    color: theme.palette.grey[600],
  },
  price: {
    color: theme.palette.positive.main,
  },
  utilityBills: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

interface Props {
  onBillsClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  priceInfo: DisplayedPriceInfo;
}

const PriceInfo: React.FC<Props> = ({ onBillsClick, priceInfo }) => {
  const { formatCurrency, formatCurrencyRange, T } = useIntl();
  const { classes } = useStyles();

  const priceString = React.useMemo(() => {
    if (priceInfo.type === 'free') {
      return T('Free');
    }

    const currencyFormat =
      priceInfo.currency === 'EUR'
        ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
        : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

    if (priceInfo.type === 'fromPrice' || priceInfo.type === 'flatPrice') {
      return formatCurrency(
        priceInfo.value,
        priceInfo.currency,
        currencyFormat,
      );
    }

    return priceInfo.valueFrom !== priceInfo.valueTo
      ? formatCurrencyRange(
          priceInfo.currency,
          priceInfo.valueFrom,
          priceInfo.valueTo,
          currencyFormat,
        )
      : formatCurrency(priceInfo.valueFrom, priceInfo.currency, currencyFormat);
  }, [T, priceInfo, formatCurrency, formatCurrencyRange]);

  return (
    <Grid item>
      <Grid container direction="row" alignItems="center">
        <Grid item>
          <Grid container direction="row" alignItems="center">
            {priceInfo.type === 'fromPrice' && (
              <Grid item>
                <Typography.Paragraph className={classes.fromLabel}>
                  {T('from_price')}
                </Typography.Paragraph>
              </Grid>
            )}
            <Grid item>
              <Typography.H2
                className={classes.price}
                data-test-locator="Redesign/Listing/ListingInfo/Price"
              >
                {priceString}
              </Typography.H2>
            </Grid>
            {priceInfo.type !== 'free' && (
              <Grid item>
                <Typography.Paragraph className={classes.month}>{`/${T(
                  'Month',
                )}`}</Typography.Paragraph>
              </Grid>
            )}
          </Grid>
        </Grid>

        {priceInfo.type !== 'free' && (
          <React.Fragment>
            <Divider
              dividerProps={{ orientation: 'vertical' }}
              className={classes.divider}
            />

            <Grid item>
              {onBillsClick ? (
                <Track name="Clicked Bills Anchor link" type="link">
                  <Link
                    to=""
                    onClick={onBillsClick}
                    className={classes.utilityBills}
                    data-test-locator="Redesign/Listing/ListingInfo/Price/Subtitle/Link"
                  >
                    <Typography.Paragraph>
                      {T(UtilityBillsLabelByValue[priceInfo.bills])}
                    </Typography.Paragraph>

                    <InformationCircleBold
                      fontSize="small"
                      className={classes.iconInfo}
                    />
                  </Link>
                </Track>
              ) : (
                <Typography.Paragraph className={classes.utilityBills}>
                  {T(UtilityBillsLabelByValue[priceInfo.bills])}
                </Typography.Paragraph>
              )}
            </Grid>
          </React.Fragment>
        )}
      </Grid>
    </Grid>
  );
};

export { PriceInfo };
